import type { DevDocsBylineConnection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';

import { createPanelDataBridge, type PanelDataBridge } from './createPanelDataBridge.ts';

export type DevDocsConnectionBridgeData = {
	contentId: string | undefined;
	devDocsConnection: DevDocsBylineConnection | null;
};

export const initialDevDocsConnectionBridgeData: DevDocsConnectionBridgeData = {
	contentId: undefined,
	devDocsConnection: null,
};

type DevDocsConnectionBridge = PanelDataBridge<DevDocsConnectionBridgeData>;

/**
 * Carries the Dev Docs source the connections byline resolved from the content
 * header's `ancestors`. See `createPanelDataBridge` for why the panel needs a
 * bridge at all.
 *
 * Kept separate from `outgoingLinksBridge` because the producers differ: links come
 * from walking the page ADF, this from a GraphQL query, so the two resolve at
 * different times and sharing one store would let each clobber the other.
 *
 * Only ever written from an effect, so nothing publishes during SSR and this
 * module-level state cannot leak between server requests.
 */
const devDocsConnectionBridge: DevDocsConnectionBridge = createPanelDataBridge(
	initialDevDocsConnectionBridgeData,
);

// Annotated because `isolatedDeclarations` cannot infer a re-exported binding.
export const publishDevDocsConnection: DevDocsConnectionBridge['publish'] =
	devDocsConnectionBridge.publish;
export const clearDevDocsConnection: DevDocsConnectionBridge['clear'] =
	devDocsConnectionBridge.clear;
export const subscribeToDevDocsConnection: DevDocsConnectionBridge['subscribe'] =
	devDocsConnectionBridge.subscribe;
export const useDevDocsConnection: DevDocsConnectionBridge['useValue'] =
	devDocsConnectionBridge.useValue;
export const clearDevDocsConnectionForTesting: DevDocsConnectionBridge['clearForTesting'] =
	devDocsConnectionBridge.clearForTesting;
